import React from 'react';
import PropTypes from 'prop-types';
import { getBaseButtonClasses, getFocusRingClasses, wrapWithLoadingSpinner, getIconSpacingClasses } from './buttonUtils';
import { brandColors, interactive } from '../../tokens/colors';

/**
 * PrimaryButton Component
 * 
 * The main call-to-action button for primary actions in the startup UI.
 * Supports solid blue and gradient variants, multiple sizes, icons, and loading states.
 * 
 * @example
 * // Solid primary button
 * <PrimaryButton onClick={handleClick}>
 *   Create Website
 * </PrimaryButton>
 * 
 * @example
 * // Gradient variant (for hero CTAs)
 * <PrimaryButton variant="gradient" size="lg">
 *   Get Started
 * </PrimaryButton>
 * 
 * @example
 * // With icon and loading state
 * <PrimaryButton
 *   leftIcon={<PlusIcon />}
 *   loading={isCreating}
 *   disabled={!canCreate}
 * >
 *   Create
 * </PrimaryButton>
 */
const PrimaryButton = ({
  children,
  onClick,
  type = 'button',
  variant = 'solid',
  size = 'md',
  disabled = false,
  loading = false,
  fullWidth = false,
  leftIcon = null,
  rightIcon = null,
  className = '',
  ...rest
}) => {
  const baseClasses = getBaseButtonClasses({ size, disabled, fullWidth, loading });
  const focusRingClasses = getFocusRingClasses('primary');

  // Variant-specific classes (theme-aware: blue in light mode, emerald in dark mode)
  const variantClasses = {
    solid: [
      interactive.primaryGradientBtn,
    ],
    gradient: [
      interactive.primaryGradientBtn,
    ],
  };

  const classes = [
    baseClasses,
    ...(variantClasses[variant] || variantClasses.solid),
    focusRingClasses,
    className,
  ].join(' ');

  const buttonContent = (
    <>
      {!loading && leftIcon && (
        <span className={getIconSpacingClasses('left', !!children)}>
          {leftIcon}
        </span>
      )}
      {wrapWithLoadingSpinner(loading, children, size)}
      {!loading && rightIcon && (
        <span className={getIconSpacingClasses('right', !!children)}>
          {rightIcon}
        </span>
      )}
    </>
  );

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={classes}
      aria-busy={loading}
      {...rest}
    >
      {buttonContent}
    </button>
  );
};

PrimaryButton.propTypes = {
  /** Button content */
  children: PropTypes.node,
  /** Click handler */
  onClick: PropTypes.func,
  /** Button type */
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  /** Visual variant */
  variant: PropTypes.oneOf(['solid', 'gradient']),
  /** Button size */
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  /** Disabled state */
  disabled: PropTypes.bool,
  /** Loading state */
  loading: PropTypes.bool,
  /** Full width button */
  fullWidth: PropTypes.bool,
  /** Icon to display on the left */
  leftIcon: PropTypes.node,
  /** Icon to display on the right */
  rightIcon: PropTypes.node,
  /** Additional CSS classes */
  className: PropTypes.string,
};

export default PrimaryButton;
